import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import {
  RatelistUnitsQuery,
  RatelistUnitsQueryVariables,
} from "@sinch/components/application/RateInterval/queries.types";
import { RatelistUnits } from "@sinch/components/application/RateInterval/queries.graphql";
import { RatelistType } from "@sinch/core/entities/serverEnums";
import { UnitType } from "@sinch/core/entities/serverEnums.types";
import { useEntityProps } from "@sinch/components/application/utilsHooks";

/**
 * Single source of truth for how a rate unit of a wagelist looks in the app: label and description.
 *
 * Use these getters wherever a rate unit of a wagelist is shown.
 * Never hardcode its label and description in a component.
 * The values come from the server, so there is no story for this hook.
 */
export const useRatelistUnit = () => {
  const { data: rateUnitsData, loading } = useQueryHandler<RatelistUnitsQuery, RatelistUnitsQueryVariables>(
    RatelistUnits,
    {
      ratelistType: RatelistType.Wage,
    }
  );

  const rateUnits: Record<UnitType, Omit<RatelistUnitsQuery["ratelistUnits"][0], "unitType">> | Record<string, never> =
    rateUnitsData?.ratelistUnits.reduce((acc, { unitType, ...unit }) => ({ ...acc, [unitType]: unit }), {}) ?? {};

  const getProp = useEntityProps(rateUnits);

  const getRateUnitDescription = getProp<string>("description");
  const getRateUnitLabel = getProp<string>("label");
  const getRateUnitUnitName = getProp<string>("unitName");

  const options =
    rateUnitsData?.ratelistUnits.map((data) => ({ value: data.unitType, label: data.label }), rateUnits) ?? [];

  return { getRateUnitDescription, getRateUnitLabel, getRateUnitUnitName, options, loading };
};
